/**
 * Authentication API for web mode
 */

import { httpClient } from './httpClient'
import { sessionStorage } from './sessionStorage'
import type { ApiResponse } from '../types'

/**
 * Login with API key
 */
export async function login(apiKey: string, type: 'bearer' | 'token' = 'bearer'): Promise<ApiResponse> {
  try {
    // Store credentials first
    sessionStorage.set({
      apiKey,
      apiType: type,
      environment: sessionStorage.getEnvironment(),
    })

    // Test connection
    const isValid = await httpClient.testConnection()

    if (!isValid) {
      // Clear invalid credentials
      sessionStorage.clear()
      return {
        success: false,
        error: 'Invalid API key',
        errorCode: 'UNAUTHORIZED',
      }
    }

    // Get the environment that was just stored
    const environment = sessionStorage.getEnvironment()

    return {
      success: true,
      data: {
        authenticated: true,
        config: {
          environment,
          apiType: type,
        },
      },
    }
  } catch (error: any) {
    // Clear credentials on failure
    sessionStorage.clear()
    return {
      success: false,
      error: error.message || 'Login failed',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Logout and clear session
 */
export async function logout(): Promise<ApiResponse> {
  try {
    sessionStorage.clear()
    return { success: true, data: { authenticated: false } }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Logout failed',
      errorCode: 'UNKNOWN_ERROR',
    }
  }
}

/**
 * Check if user is authenticated
 */
export async function check(): Promise<ApiResponse> {
  try {
    const isAuthenticated = sessionStorage.isAuthenticated()
    const session = sessionStorage.get()

    return {
      success: true,
      data: {
        isAuthenticated,
        config: {
          environment: sessionStorage.getEnvironment(),
          apiType: session?.apiType || 'bearer',
        },
      },
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Auth check failed',
      errorCode: 'UNKNOWN_ERROR',
    }
  }
}

/**
 * Set environment (staging or production)
 */
export async function setEnvironment(env: 'staging' | 'production'): Promise<ApiResponse> {
  try {
    sessionStorage.setEnvironment(env)

    return {
      success: true,
      data: { environment: env },
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to set environment',
      errorCode: 'UNKNOWN_ERROR',
    }
  }
}

/**
 * Get current environment
 */
export async function getEnvironment(): Promise<ApiResponse> {
  try {
    const environment = sessionStorage.getEnvironment()

    return {
      success: true,
      data: { environment },
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get environment',
      errorCode: 'UNKNOWN_ERROR',
    }
  }
}
